import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/24/outline";
import { Stack } from "@/core/components/ui/stack";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/core/components/ui/tooltip";
import type { ApReviewQueueItem } from "@/accounts-payable/types";
import { useBreakpoints } from "@/core/component-hooks/use-breakpoints";
import { cn } from "@/core/utils/utils";
import { ApInvoiceSegmentPreview } from "./invoice-segment-preview";

const NAV_BTN =
  "flex h-8 w-8 flex-none items-center justify-center rounded-full border border-stroke-tertiary bg-card text-secondary-text transition-colors hover:border-stroke-secondary hover:bg-bg-secondary/60 hover:text-primary-text disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-card disabled:hover:text-secondary-text";

export const getCarouselWindow = (
  total: number,
  currentIndex: number,
  maxVisible: number,
) => {
  if (total <= 0 || currentIndex < 0 || maxVisible <= 0) return [];

  const visibleCount = Math.min(total, maxVisible);
  const preferredStart = currentIndex - Math.floor(visibleCount / 2);
  const start = Math.max(0, Math.min(preferredStart, total - visibleCount));

  return Array.from({ length: visibleCount }, (_, offset) => start + offset);
};

export const getCarouselVisibleCount = ({
  isSm,
  isMd,
  isLg,
}: {
  isSm: boolean;
  isMd: boolean;
  isLg: boolean;
}) => {
  if (isSm) return 3;
  if (isMd) return 5;
  if (isLg) return 7;
  return 9;
};

export const getCarouselSegmentTier = (index: number, currentIndex: number) => {
  const distance = Math.abs(index - currentIndex);
  if (distance === 0) return "active" as const;
  if (distance === 1) return "near" as const;
  if (distance === 2) return "middle" as const;
  return "far" as const;
};

export const findFirstNewQueueItem = (
  pages: { items: ApReviewQueueItem[] }[] | undefined,
  loadedIds: string[],
) => {
  const loadedIdSet = new Set(loadedIds);
  return pages
    ?.flatMap((page) => page.items)
    .find((item) => !loadedIdSet.has(item.id));
};

export const getCarouselKeyboardDirection = (
  key: string,
  target: EventTarget | null,
) => {
  const element = target instanceof HTMLElement ? target : null;
  if (
    element &&
    (element.closest(
      'input, textarea, select, [contenteditable="true"], [role="combobox"], [role="listbox"], [role="menu"], [role="dialog"]',
    ) ||
      element.isContentEditable)
  ) {
    return null;
  }
  /**
   * An open modal owns the keyboard. The listener is on `window`, so arrow
   * keys reach it even from inside a dialog — and stepping to another invoice
   * behind one is worse than a no-op: the manual job-match form keeps its
   * selected job while `invoiceId` changes underneath it, so confirming would
   * attribute that job to a different invoice. The overlay and other portalled
   * furniture sit outside `[role="dialog"]`, hence the document-level check
   * rather than relying on the target alone.
   */
  const doc = element?.ownerDocument ?? globalThis.document;
  if (doc?.querySelector('[role="dialog"][data-state="open"]')) return null;
  if (key === "ArrowLeft") return "previous" as const;
  if (key === "ArrowRight") return "next" as const;
  return null;
};

export const ReviewQueueCarousel = ({
  items,
  currentIndex,
  hasNextPage,
  isFetchingNextPage,
  onSelect,
  onPrevious,
  onNext,
}: {
  items: ApReviewQueueItem[];
  currentIndex: number;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onSelect: (id: string) => void;
  onPrevious: () => void;
  onNext: () => void;
}) => {
  const { isSm, isMd, isLg } = useBreakpoints();
  const visibleCount = getCarouselVisibleCount({ isSm, isMd, isLg });
  const visibleIndices = getCarouselWindow(
    items.length,
    currentIndex,
    visibleCount,
  );
  const canGoPrevious = currentIndex > 0;
  const canGoNext = currentIndex < items.length - 1 || hasNextPage;

  return (
    <TooltipProvider delayDuration={150} disableHoverableContent>
      <Stack
        direction="row"
        gap="sm"
        align="center"
        justify="center"
        className="max-w-full"
      >
        <button
          type="button"
          aria-label="Previous invoice"
          disabled={!canGoPrevious}
          onClick={onPrevious}
          className={NAV_BTN}
        >
          <ArrowLeftIcon className="h-4 w-4" />
        </button>
        <Stack
          direction="row"
          gap="sm"
          align="center"
          role="group"
          aria-label="Invoice review queue"
          className="min-w-0"
        >
          {visibleIndices.map((index) => {
            const item = items[index];
            const isCurrent = index === currentIndex;
            const tier = getCarouselSegmentTier(index, currentIndex);

            return (
              <Tooltip key={item.id}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={`Go to ${item.vendor_name} (${index + 1} of ${items.length})`}
                    aria-current={isCurrent}
                    onClick={() => onSelect(item.id)}
                    className={cn(
                      "group flex h-8 flex-none items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                      {
                        "w-10": tier === "active",
                        "w-8": tier === "near",
                        "w-6": tier === "middle",
                        "w-4": tier === "far",
                      },
                    )}
                  >
                    <span
                      className={cn(
                        "w-full rounded-full bg-stroke-tertiary transition-all group-hover:bg-stroke-secondary",
                        {
                          "h-2.5 bg-primary opacity-100 shadow-sm group-hover:bg-primary":
                            tier === "active",
                          "h-2 opacity-80": tier === "near",
                          "h-1.5 opacity-60": tier === "middle",
                          "h-1 opacity-40": tier === "far",
                        },
                      )}
                    />
                  </button>
                </TooltipTrigger>
                {/* `lg` leaves room for the 320px preview plus padding; at `md`
                    the cap included the padding and clipped its right edge. */}
                <TooltipContent maxWidth="lg" tone="card" className="p-4">
                  <ApInvoiceSegmentPreview
                    invoice={item}
                    actions={item.last_completed_agent_run.actions}
                  />
                </TooltipContent>
              </Tooltip>
            );
          })}
        </Stack>
        <button
          type="button"
          aria-label="Next invoice"
          disabled={!canGoNext || isFetchingNextPage}
          onClick={onNext}
          className={NAV_BTN}
        >
          <ArrowRightIcon className="h-4 w-4" />
        </button>
        <span className="ml-1 flex-none whitespace-nowrap text-sm font-medium text-tertiary-text">
          {currentIndex + 1} / {items.length}
          {hasNextPage ? "+" : ""}
        </span>
      </Stack>
    </TooltipProvider>
  );
};
